import { API_URL } from '../../config';
import React, { useState, useRef, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  DirectionsRun as RunIcon,
  Warning as WarningIcon,
  Explore as ExploreIcon,
} from '@mui/icons-material';

import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useAuthStore } from '../../store/authStore';
import { useSocketStore } from '../../store/socketStore';
import { useLocationStore } from '../../store/locationStore';
import '../../assets/map-styles.css';
import { findClosestNode, findShortestPath, generateVoiceInstructions, Hazard } from '../../utils/pathfinding';



// Georeference constants matching Locations.tsx
export const PROPERTY_CONFIG = {
  ANCHOR_LAT: 40.7128,      // The real-world Lat of Godot's (0,0)
  ANCHOR_LNG: -74.0060,     // The real-world Lng of Godot's (0,0)
  SCALE_LAT: 0.000008983,   // ~1 meter in degrees latitude
  SCALE_LNG: 0.000011831,   // ~1 meter in degrees longitude
  ROTATION_RAD: 0.25,       // Building offset from True North in radians
};

export const MAP_BOUNDS = {
  xMin: -12.0,
  xMax: 8.0,
  yMin: -18.0,
  yMax: 13.0
};

export const IMAGE_BOUNDS = {
  xMin: -9.0,
  xMax: 5.0,
  yMin: -15.0,
  yMax: 10.0
};

export function godotToLatLng(x: number, y: number) {
  const theta = PROPERTY_CONFIG.ROTATION_RAD;
  const cosTheta = Math.cos(theta);
  const sinTheta = Math.sin(theta);

  const eastOffset = x * cosTheta - y * sinTheta;
  const northOffset = -x * sinTheta - y * cosTheta;

  const lat = PROPERTY_CONFIG.ANCHOR_LAT + northOffset * PROPERTY_CONFIG.SCALE_LAT;
  const lng = PROPERTY_CONFIG.ANCHOR_LNG + eastOffset * PROPERTY_CONFIG.SCALE_LNG;

  return [lat, lng] as [number, number];
}

export function latLngToGodot(lat: number, lng: number) {
  const theta = PROPERTY_CONFIG.ROTATION_RAD;
  const cosTheta = Math.cos(theta);
  const sinTheta = Math.sin(theta);

  // Relative offsets in degrees
  const dLat = lat - PROPERTY_CONFIG.ANCHOR_LAT;
  const dLng = lng - PROPERTY_CONFIG.ANCHOR_LNG;

  // Relative offsets in meters
  const northOffset = dLat / PROPERTY_CONFIG.SCALE_LAT;
  const eastOffset = dLng / PROPERTY_CONFIG.SCALE_LNG;

  // Rotate back (inverse rotation)
  const x = eastOffset * cosTheta - northOffset * sinTheta;
  const y = -eastOffset * sinTheta - northOffset * cosTheta;

  return [x, y] as [number, number];
}

export const GODOT_MAX_X = 800;
export const GODOT_MAX_Y = 600;

export function godotToSchematic(x: number, y: number) {
  const pixelX = ((x - IMAGE_BOUNDS.xMin) / (IMAGE_BOUNDS.xMax - IMAGE_BOUNDS.xMin)) * GODOT_MAX_X;
  const pixelY = (1 - ((y - IMAGE_BOUNDS.yMin) / (IMAGE_BOUNDS.yMax - IMAGE_BOUNDS.yMin))) * GODOT_MAX_Y;
  return [pixelY, pixelX] as [number, number];
}

export const getSchematicLatLng = (item: any) => {
  const lat = Number(item.latitude);
  const lng = Number(item.longitude);

  if (isNaN(lat) || isNaN(lng)) {
    return godotToSchematic(0, 0);
  }

  // 1. Real GPS coordinates
  if (lat > 40.0 && lat < 41.5 && lng > -74.5 && lng < -73.0) {
    const [x, y] = latLngToGodot(lat, lng);
    return godotToSchematic(x, y);
  }

  // 2. Godot world coordinates
  if (lat >= -50 && lat <= 50 && lng >= -50 && lng <= 50) {
    return godotToSchematic(lat, lng);
  }

  // 3. Legacy pixel coordinates
  const worldX = -9.0 + (lng / 800) * 14.0;
  const worldY = -15.0 + (lat / 600) * 25.0;
  return godotToSchematic(worldX, worldY);
};



// Haversine distance formula
function getDistanceInMeters(lat1: number, lon1: number, lat2: number, lon2: number) {
  const R = 6371e3; // Earth radius in meters
  const phi1 = lat1 * Math.PI / 180;
  const phi2 = lat2 * Math.PI / 180;
  const deltaPhi = (lat2 - lat1) * Math.PI / 180;
  const deltaLambda = (lon2 - lon1) * Math.PI / 180;

  const a = Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
            Math.cos(phi1) * Math.cos(phi2) *
            Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return Math.round(R * c);
}

export default function GuestMap() {
  const { user } = useAuthStore();
  const propertyId = user?.property_id || 2;
  const [selectedFloor, setSelectedFloor] = useState('1');
  const [calculatedRoute, setCalculatedRoute] = useState<[number, number][] | null>(null);
  const [routeInstructions, setRouteInstructions] = useState<string[]>([]);
  const hasSpokenRef = useRef(false);
  const lastRouteRef = useRef<string | null>(null);
  const { socket } = useSocketStore();

  // Set default floor from room number on mount
  useEffect(() => {
    if (user?.room_number) {
      const roomStr = String(user.room_number).trim();
      if (roomStr.startsWith('2')) {
        setSelectedFloor('2');
      } else {
        setSelectedFloor('1');
      }
    }
  }, [user]);

  // Fetch Guest's Own Latest Coordinates
  const { data: locationHistory = [], isLoading: loadingLocation } = useQuery({
    queryKey: ['guest-location-history', user?.id],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/locations/history/${user?.id}`);
      return res.data.history;
    },
    enabled: Boolean(user?.id),
    refetchInterval: 3000, // Frequent updates for live tracking
  });

  // Emit guest location to server so admins can track us
  useEffect(() => {
    if (!socket || !user?.id) return;

    // When location history updates, broadcast our position via socket
    if (locationHistory.length > 0) {
      const latest = locationHistory[0];
      socket.emit('location_update', {
        userId: user.id,
        latitude: latest.latitude,
        longitude: latest.longitude,
        beaconId: latest.beacon_id,
        zoneId: latest.zone_id,
      });
    }
  }, [socket, user?.id, locationHistory]);

  // Fetch Zones (Assembly Points/Exits)
  const { data: zones = [], isLoading: loadingZones } = useQuery({
    queryKey: ['zones', propertyId],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/locations/zones/${propertyId}`);
      return res.data.zones;
    },
    enabled: Boolean(propertyId),
  });

  // Fetch Active Incidents
  const { data: incidents = [], isLoading: loadingIncidents } = useQuery({
    queryKey: ['incidents', propertyId],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/crisis/active`, { params: { propertyId } });
      return res.data.incidents;
    },
    enabled: Boolean(propertyId),
    refetchInterval: 5000,
  });

  // Current georeferenced position of the user
  const userPosition = useMemo(() => {
    if (locationHistory.length > 0) {
      return getSchematicLatLng(locationHistory[0]);
    }
    // Fallback default
    return godotToSchematic(0, 0);
  }, [locationHistory]);

  const mapCenter = userPosition;



  // Safe Exit Assembly Points (Dynamic from DB + Mock Fallback)
  const assemblyPoints = useMemo(() => {
    const dbExits = zones.filter(
      (z: any) => z.zone_type === 'safe_zone' || z.zone_type === 'assembly_point' || z.zone_type === 'exit'
    );
    if (dbExits.length > 0) {
      return dbExits.map((z: any) => {
        let lat = userPosition[0];
        let lng = userPosition[1];
        if (z.coordinates) {
          try {
            const geom = typeof z.coordinates === 'string' ? JSON.parse(z.coordinates) : z.coordinates;
            if (geom.type === 'Point') {
              lat = geom.coordinates[1];
              lng = geom.coordinates[0];
            } else if (geom.type === 'Polygon' || geom.type === 'MultiPolygon') {
              const coordsList = geom.coordinates[0];
              if (Array.isArray(coordsList) && coordsList.length > 0) {
                const firstPoint = coordsList[0];
                lng = firstPoint[0];
                lat = firstPoint[1];
              }
            }
          } catch (e) {
            console.error('Failed to parse zone coordinates:', e);
          }
        }
        return {
          id: z.id,
          name: z.name,
          latitude: lat,
          longitude: lng,
          capacity: z.capacity,
          occupancy: z.current_occupancy
        };
      });
    }

    return [
      { id: 'mock-a', name: 'SAFE ASSEMBLY GATE A (NORTH)', latitude: PROPERTY_CONFIG.ANCHOR_LAT + 0.0012, longitude: PROPERTY_CONFIG.ANCHOR_LNG - 0.0012, capacity: 400, occupancy: 0 },
      { id: 'mock-b', name: 'SAFE ASSEMBLY GATE B (SOUTH)', latitude: PROPERTY_CONFIG.ANCHOR_LAT - 0.0012, longitude: PROPERTY_CONFIG.ANCHOR_LNG + 0.0012, capacity: 600, occupancy: 0 }
    ];
  }, [zones]);

  // Find nearest exit to the guest
  const nearestExit = useMemo(() => {
    if (assemblyPoints.length === 0) return null;
    let nearest = assemblyPoints[0];
    let minDistance = Infinity;

    assemblyPoints.forEach((exit: any) => {
      const exitPos = getSchematicLatLng(exit);
      const dx = userPosition[1] - exitPos[1];
      const dy = userPosition[0] - exitPos[0];
      const dist = Math.sqrt(dx*dx + dy*dy); // Distance in pixels
      if (dist < minDistance) {
        minDistance = dist;
        nearest = exit;
      }
    });

    return {
      ...nearest,
      distance: minDistance,
    };
  }, [assemblyPoints, userPosition]);

  const activeIncident = incidents.length > 0 ? incidents[0] : null;

  // Distance and ETA to nearest exit
  const distanceToExit = useMemo(() => {
    if (!nearestExit || !locationHistory.length) return null;
    const latest = locationHistory[0];
    const lat1 = Number(latest.latitude);
    const lng1 = Number(latest.longitude);
    if (isNaN(lat1) || isNaN(lng1)) return null;

    // Convert exit to lat/lng for distance calc
    const exitLat = Number(nearestExit.latitude);
    const exitLng = Number(nearestExit.longitude);
    if (isNaN(exitLat) || isNaN(exitLng)) return null;

    const meters = getDistanceInMeters(lat1, lng1, exitLat, exitLng);
    const walkingMinutes = Math.ceil(meters / 80); // ~80m/min walking speed
    return { meters, walkingMinutes };
  }, [nearestExit, locationHistory]);

  // Calculate proper route
  useEffect(() => {
    const getRoute = async () => {
      if (!activeIncident || !userPosition) return;
      
      const hazards: Hazard[] = incidents.map((inc: any) => {
        let x = 0, y = 0;
        if (inc.latitude && inc.longitude) {
           const [gx, gy] = latLngToGodot(Number(inc.latitude), Number(inc.longitude));
           x = gx; y = gy;
        }
        return { x, y, floor: Number(selectedFloor), radius: 5.0 };
      });

      const userGodotX = IMAGE_BOUNDS.xMin + (userPosition[1] / GODOT_MAX_X) * (IMAGE_BOUNDS.xMax - IMAGE_BOUNDS.xMin);
      const userGodotY = IMAGE_BOUNDS.yMin + ((GODOT_MAX_Y - userPosition[0]) / GODOT_MAX_Y) * (IMAGE_BOUNDS.yMax - IMAGE_BOUNDS.yMin);
      
      const startNode = findClosestNode(userGodotX, userGodotY, Number(selectedFloor));
      
      if (startNode) {
         const route = findShortestPath(startNode.id, hazards);
         if (route && route.path) {
           const coords = route.path.map((n: any) => {
              return godotToSchematic(n.x, n.y);
           });
           setCalculatedRoute(coords);

           const instructions = generateVoiceInstructions(route.path);
           setRouteInstructions(instructions);

           // Re-announce when route changes significantly
           const routeKey = instructions[0] || '';
           if (routeKey !== lastRouteRef.current && instructions.length > 0 && 'speechSynthesis' in window) {
             window.speechSynthesis.cancel();
             const utterance = new SpeechSynthesisUtterance(instructions[0]);
             window.speechSynthesis.speak(utterance);
             lastRouteRef.current = routeKey;
             hasSpokenRef.current = true;
           }

         } else {
           setCalculatedRoute(null);
           setRouteInstructions(['Shelter in place immediately. All routes are blocked.']);
           if (!hasSpokenRef.current && 'speechSynthesis' in window) {
              const utterance = new SpeechSynthesisUtterance('Shelter in place immediately. All routes are blocked.');
              window.speechSynthesis.speak(utterance);
              hasSpokenRef.current = true;
           }
         }
      }
    };
    getRoute();
  }, [userPosition, activeIncident, incidents, selectedFloor]);

  if (loadingLocation || loadingZones || loadingIncidents) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '60vh', gap: 2 }}>
        <CircularProgress color="primary" />
        <Typography variant="body2" color="text.secondary">Loading live map & evacuation routes...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', height: 'calc(100vh - 150px)', width: '100%' }}>
      <style>{`
        @keyframes pulse {
          0% { transform: scale(0.5); opacity: 0.8; }
          100% { transform: scale(1.5); opacity: 0; }
        }
        @keyframes flash {
          0% { transform: scale(0.9); opacity: 0.9; }
          100% { transform: scale(1.1); opacity: 1; box-shadow: 0 0 20px rgba(239, 68, 68, 1); }
        }
        .leaflet-container {
          background-color: var(--bg-card) !important;
        }
        @keyframes route-dash-flow {
          0% { stroke-dashoffset: 0; }
          100% { stroke-dashoffset: -40; }
        }
        .leaflet-overlay-pane svg path {
          animation: route-dash-flow 1.5s linear infinite;
        }
        .map-status-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          display: inline-block;
        }
        .map-status-dot--live {
          background-color: #10b981;
          box-shadow: 0 0 6px #10b981;
        }
        .map-status-dot--polling {
          background-color: #f59e0b;
          box-shadow: 0 0 6px #f59e0b;
        }
      `}</style>

      {/* Floating Floor Plan / View Mode Controls */}
      <Paper
        sx={{
          position: 'absolute',
          top: 16,
          left: 16,
          zIndex: 1000,
          background: 'var(--bg-glass)',
          border: '1px solid var(--border-medium)',
          backdropFilter: 'blur(10px)',
          p: 0.5,
          borderRadius: 2,
        }}
        elevation={6}
      >
        <ToggleButtonGroup
          value={selectedFloor}
          exclusive
          onChange={(e, val) => val && setSelectedFloor(val)}
          size="small"
        >
          <ToggleButton value="1" sx={{ px: 2, fontWeight: 700, color: '#fff', '&.Mui-selected': { bgcolor: 'rgba(59, 130, 246, 0.2)' } }}>FLOOR 1</ToggleButton>
          <ToggleButton value="2" sx={{ px: 2, fontWeight: 700, color: '#fff', '&.Mui-selected': { bgcolor: 'rgba(59, 130, 246, 0.2)' } }}>FLOOR 2</ToggleButton>
        </ToggleButtonGroup>
      </Paper>

      {/* Live Connection Status */}
      <Paper
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 1000,
          background: 'rgba(15, 23, 42, 0.9)',
          border: '1px solid rgba(255,255,255,0.1)',
          backdropFilter: 'blur(10px)',
          p: '6px 12px',
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
        elevation={6}
      >
        <span className={`map-status-dot ${socket?.connected ? 'map-status-dot--live' : 'map-status-dot--polling'}`} />
        <Typography variant="caption" sx={{ color: socket?.connected ? '#10b981' : '#f59e0b', fontWeight: 800, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {socket?.connected ? 'LIVE TRACKING' : 'POLLING'}
        </Typography>
      </Paper>

      {/* Main Map Container */}
      <iframe 
        title="Mappedin Map" 
        name="Mappedin Map" 
        allow="clipboard-write 'self' https://app.mappedin.com; web-share 'self' https://app.mappedin.com" 
        scrolling="no" 
        width="100%" 
        height="100%" 
        frameBorder="0" 
        style={{ border: 0, borderRadius: 12 }} 
        src="https://app.mappedin.com/map/6a2d1e9d8c2010000b751066?embedded=true"
      ></iframe>

      {/* Floating Evacuation Status Panel */}
      <Card
        sx={{
          position: 'absolute',
          bottom: 16,
          left: 16,
          right: 16,
          zIndex: 1000,
          background: activeIncident ? 'rgba(15, 23, 42, 0.9)' : 'var(--bg-glass)',
          backdropFilter: 'blur(16px)',
          border: activeIncident ? '1.5px solid rgba(16, 185, 129, 0.4)' : '1px solid var(--border-medium)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
          borderRadius: 3,
        }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          {activeIncident ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <RunIcon sx={{ color: '#10b981' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#10b981' }}>
                    EVACUATION ROUTE ACTIVE
                  </Typography>
                </Box>
                {nearestExit && (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Chip
                      label={distanceToExit ? `${distanceToExit.meters}m away` : 'Nearest exit located'}
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(16, 185, 129, 0.2)',
                        color: '#34d399',
                        fontWeight: 800,
                        fontSize: '0.75rem',
                      }}
                    />
                    {distanceToExit && (
                      <Chip
                        label={`~${distanceToExit.walkingMinutes} min walk`}
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(251, 191, 36, 0.15)',
                          color: '#fbbf24',
                          fontWeight: 700,
                          fontSize: '0.72rem',
                        }}
                      />
                    )}
                  </div>
                )}
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#fff', mb: 0.5 }}>
                  Directions to safe exit:
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>
                  {routeInstructions.length > 0 
                    ? routeInstructions[0]
                    : nearestExit 
                      ? `Follow the green dashed evacuation path towards ${nearestExit.name}.`
                      : 'Follow the evacuation signs towards the nearest exit stairwell.'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#fbbf24', fontWeight: 600, mt: 1, display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.8rem' }}>
                  <WarningIcon sx={{ fontSize: 16 }} /> Do not use elevators. Stay low to avoid smoke.
                </Typography>
                {routeInstructions.length > 0 && (
                   <Chip 
                     label="🔊 Play Voice Instructions" 
                     color="primary" 
                     size="small" 
                     onClick={() => {
                        if ('speechSynthesis' in window) {
                          window.speechSynthesis.cancel();
                          const utterance = new SpeechSynthesisUtterance(routeInstructions[0]);
                          window.speechSynthesis.speak(utterance);
                        }
                     }}
                     sx={{ mt: 1.5, fontWeight: 700, cursor: 'pointer' }}
                   />
                )}
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <ExploreIcon sx={{ color: 'var(--accent-blue)' }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Property Safe & Monitored
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  No active emergencies. You can view the layout of Floor {selectedFloor} above.
                </Typography>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
